/**
 * useMSG91Script
 *
 * Injects the MSG91 OTP widget script once into the document head and returns
 * a boolean `ready` that flips true once `window.initSendOTP` is available.
 *
 * - Idempotent: calling the hook from multiple components is safe; the script
 *   tag is only created once per page lifecycle.
 * - No-op when VITE_MSG91_WIDGET_ID is not set (local dev fallback path).
 */
import { useEffect, useState } from "react";
const MSG91_SCRIPT_URL = "https://verify.msg91.com/otp-provider.js";
const SCRIPT_ID = "msg91-otp-provider";
let scriptPromise = null;
function loadScript() {
    if (scriptPromise)
        return scriptPromise;
    // Already loaded (e.g. HMR reload).
    if (document.getElementById(SCRIPT_ID)) {
        scriptPromise = Promise.resolve();
        return scriptPromise;
    }
    scriptPromise = new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.id = SCRIPT_ID;
        script.src = MSG91_SCRIPT_URL;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => {
            scriptPromise = null; // Allow retry on next mount.
            reject(new Error("Failed to load MSG91 OTP widget script"));
        };
        document.head.appendChild(script);
    });
    return scriptPromise;
}
export function useMSG91Script() {
    const widgetId = import.meta.env.VITE_MSG91_WIDGET_ID;
    const [ready, setReady] = useState(false);
    const [error, setError] = useState(null);
    useEffect(() => {
        // Skip if the widget is not configured — dev fallback path.
        if (!widgetId)
            return;
        loadScript()
            .then(() => setReady(true))
            .catch((err) => {
            setError(err instanceof Error ? err.message : "MSG91 script failed to load");
        });
    }, [widgetId]);
    return { ready, error };
}
